import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
  ElementRef,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MatNativeDateModule,
  DateAdapter,
  MAT_DATE_LOCALE,
  NativeDateAdapter,
} from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  ApiService,
  Airport,
  PassengerCount,
} from '../../../core/services/api.service';
import { NotificationService } from '../../../core/services/notification.service';
import { Router } from '@angular/router';
import { IndexedDbService } from '../../../core/services/indexed-dbservice.service';
import { CustomDatePipe } from '../../../Shared/pipes/customDate.pipe';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import { AppConstants_settings } from '../../../core/AppConstants_Appsettings.enum';
import { HttpClient } from '@angular/common/http';
import { LangPipe } from '../../../Shared/pipes/lang.pipe';

@Component({
  selector: 'app-flight-request',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatRadioModule,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatSnackBarModule,
    LangPipe,
  ],
  templateUrl: './flight-request.component.html',
  providers: [CustomDatePipe],
})
export class FlightRequestComponent implements OnInit {
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  flightForm!: FormGroup;
  tripType: 'oneway' | 'roundtrip' | 'multicity' = 'oneway';
  passengerCount: PassengerCount = { adult: 1, child: 0, infant: 0 };
  airports: Airport[] = [];
  minDate = new Date();
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1));
  private searchAirport$ = new Subject<string>();
  totalPassengers = 1;
  isPax = false;
  loading = false;
  journeyDate: any;
  journeyDayName: any;
  returnDate: any;
  returnDayName: any;
  today: Date = new Date();
  groupedAirports: any[] = [];
  isSwapping: boolean = false;
  dynamicFormat: string = 'MM/dd/yyyy';
  popularDestinations: any[] = [];
  showFromPopular: boolean = false;
  showToPopular: boolean = false;
  isOptionSelected: boolean = false;
  journeyDateMulti: any[] = [];
  showAirlineDropdown = false;
  selectedAirline = '';
  airlines: any[] = [];
  toggleFilter: boolean = false;
  airlineList: any[] = [];
  suppliers: any[] = [];
  selectedSupplierCodes: string[] = [];
  showSupplierToAgency: any;
  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private notification: NotificationService,
    private router: Router,
    private dbService: IndexedDbService,
    public datePipe: CustomDatePipe,
    public date: DatePipe,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private elementRef: ElementRef,
  ) {
    registerLocaleData(localeAr);
    this.searchAirport$
      .pipe(
        debounceTime(400),
        switchMap((term) =>
          term.length >= 3 ? this.apiService.searchAirports(term) : of([]),
        ),
      )
      .subscribe((res) => {
        // group main + nearby
        this.groupedAirports = (res || []).map((airport: any) => ({
          main: airport,
          nearby: airport.nearbyAirports || [],
        }));
        if (res.length == 0) {
          this.groupedAirports = [
            {
              main: { airportName: 'No Airport Found', airportCode: '' },
              nearby: [],
            },
          ];
        }
      });

    this.dbService.getData('tokenData').then((data) => {
      if (data) {
        this.showSupplierToAgency = data?.ShowSupplierToAgency;
      }
    });
  }

  async ngOnInit() {
    this.buildForm();
    this.updatePassengerLabel();
    this.onFlightTypeChange('oneway');
    this.patchFlightForm();
    this.getAirlines();
    this.getSupplierList();
    this.dynamicFormat = await this.datePipe.getFormat();
  }

  private buildForm() {
    this.flightForm = this.fb.group({
      tripType: ['oneway', Validators.required],
      cabinClass: ['economy', Validators.required],
      passengers: this.fb.group({
        adult: [1, [Validators.required, Validators.min(1), Validators.max(9)]],
        child: [0, [Validators.min(0), Validators.max(8)]],
        infant: [0, [Validators.min(0), Validators.max(4)]],
      }),
      generalInfo: this.fb.group({
        companyId: [''],
        apiKey: [''],
        channel: [''],
        companyCurrency: [''],
        agencyCurrency: ['AED'],
        grossCurrency: ['AED'],
        agentId: [0],
        agencyUserId: [0],
        agencyBranchId: [0],
        companyBranchId: [0],
        companyUserId: [0],
        agencyGroupId: [0],
        companyDecimalPref: [2],
        addFlightMarkupInBaseFare: [true],
      }),
      FilterOptions: this.fb.group({
        PreferredAirlines: [''],
        DirectFlightOnly: [false],
        ResultType: ['Combined'],
        supplierCode: [[]],
      }),
      // oneway/roundtrip
      from: [''],
      to: [''],
      departureDate: [''],
      returnDate: [''],
      ui_From: [''],
      ui_To: [''],
      ui_DepartureDate: [''],
      ui_ReturnDate: [''],
      // multicity
      multiOriginDestinations: this.fb.array([] as Array<FormGroup>),
    });
  }

  get originDestinations(): FormArray {
    return this.flightForm.get('multiOriginDestinations') as FormArray;
  }

  async patchFlightForm() {
    const data = await this.dbService.getData('request-data');
    if (!data) return;

    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    this.flightForm.patchValue({
      tripType: data.tripType,
      cabinClass: data.cabinClass,
      passengers: data.passengers,
      generalInfo: data.generalInfo,
      FilterOptions: data.FilterOptions,
    });

    const savedSupplierCodes = data.FilterOptions?.SupplierCode || data.FilterOptions?.supplierCode;
    if (Array.isArray(savedSupplierCodes) && savedSupplierCodes.length > 0) {
      if (savedSupplierCodes.length === 1 && typeof savedSupplierCodes[0] === 'string' && savedSupplierCodes[0].includes(',')) {
        this.selectedSupplierCodes = savedSupplierCodes[0]
          .split(',')
          .map((x: string) => x.trim())
          .filter((x: string) => x !== '');
      } else {
        this.selectedSupplierCodes = savedSupplierCodes.map((x: any) => String(x).trim());
      }
    } else {
      this.selectedSupplierCodes = [];
    }
    const formVal = this.selectedSupplierCodes.length > 0 ? [this.selectedSupplierCodes.join(',')] : [];
    this.flightForm.get('FilterOptions.supplierCode')?.setValue(formVal);

    this.onFlightTypeChange(data.tripType);
    this.updatePassengerLabel();

    const odArray = this.originDestinations;
    odArray.clear();
    if (data.tripType === 'multicity' && data.multiOriginDestinations?.length) {
      data.multiOriginDestinations.forEach((od: any) => {
        const depDate = new Date(od.departureDate);
        const safeDep =
          depDate < today
            ? today.toISOString().split('T')[0]
            : od.departureDate;

        odArray.push(
          this.fb.group({
            from: [od.from, Validators.required],
            to: [od.to, Validators.required],
            departureDate: [safeDep, Validators.required],
            ui_From: [od.ui_From || ''],
            ui_To: [od.ui_To || ''],
          }),
        );
      });
    } else {
      const dep = new Date(data.departureDate);
      const safeDep =
        dep < today ? today.toISOString().split('T')[0] : data.departureDate;
      let safeRet = '';
      if (data.tripType === 'roundtrip') {
        const ret = new Date(data.returnDate);
        safeRet =
          ret < today ? tomorrow.toISOString().split('T')[0] : data.returnDate;
      }

      this.flightForm.patchValue({
        from: data.from || '',
        to: data.to || '',
        departureDate: safeDep,
        returnDate: data.tripType === 'roundtrip' ? safeRet : '',
        ui_From: data.ui_From || '',
        ui_To: data.ui_To || '',
        ui_DepartureDate: data.ui_DepartureDate || '',
        ui_ReturnDate:
          data.tripType === 'roundtrip' ? data.ui_ReturnDate || '' : '',
      });
    }
  }

  async swapFromTo() {
    const from = this.flightForm.get('from')?.value;
    const to = this.flightForm.get('to')?.value;

    const uiFrom = this.flightForm.get('ui_From')?.value;
    const uiTo = this.flightForm.get('ui_To')?.value;
    setTimeout(() => {
      this.flightForm.patchValue(
        {
          from: to,
          to: from,
          ui_From: uiTo,
          ui_To: uiFrom,
        },
        { emitEvent: false },
      );
    }, 200);
  }

  onFromFocus(event: any, type: 'from' | 'to') {
    if (this.isOptionSelected) return;
    if (type === 'from') {
      this.showFromPopular = true;
      this.showToPopular = false;
    } else {
      this.showToPopular = true;
      this.showFromPopular = false;
    }
    event.target.select();
    this.http.get('assets/destination-list.json').subscribe((res: any) => {
      this.popularDestinations = res;
      if (type === 'from') {
        this.showFromPopular = true;
      } else {
        this.showToPopular = true;
      }
    });
  }

  getAirlines() {
    this.http.get('assets/airlines.json').subscribe((res: any) => {
      this.airlines = res;
    });
  }

  async getSupplierList() {
    const tokenRaw = await this.dbService.getData('tokenData');
    let userData: any;
    if (tokenRaw) {
      userData = tokenRaw;
    }
    const channel = AppConstants_settings.CHANNEL;
    const companyId =
      AppConstants_settings.COMPANY_ID || userData?.CompanyId || 'NS';
    const companyBranchId =
      userData?.CompanyBranchId || userData?.AgencyBranchId || 1;
    const agentId = userData?.AgentId || 0;

    this.apiService
      .getSupplierList(channel, companyId, companyBranchId, agentId)
      .subscribe({
        next: (res: any) => {
          if (res && res.statuscode === 1 && res.data) {
            try {
              this.suppliers =
                typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

              // Default check all if selectedSupplierCodes is empty
              if (this.selectedSupplierCodes.length === 0) {
                this.selectedSupplierCodes = this.suppliers.map(
                  (s) => s.SupplierCode,
                );
                const val =
                  this.selectedSupplierCodes.length > 0
                    ? [this.selectedSupplierCodes.join(',')]
                    : [];
                this.flightForm
                  .get('FilterOptions.supplierCode')
                  ?.setValue(val);
              }
            } catch (e) {
              console.error('Failed to parse supplier list:', e);
            }
          }
        },
        error: (err) => {
          console.error('Error fetching supplier list:', err);
        },
      });
  }

  onSupplierChange(supplierCode: string, event: any) {
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.selectedSupplierCodes.includes(supplierCode)) {
        this.selectedSupplierCodes.push(supplierCode);
      }
    } else {
      this.selectedSupplierCodes = this.selectedSupplierCodes.filter(
        (c) => c !== supplierCode,
      );
    }
    const val =
      this.selectedSupplierCodes.length > 0
        ? [this.selectedSupplierCodes.join(',')]
        : [];
    this.flightForm.get('FilterOptions.supplierCode')?.setValue(val);
  }

  isAllSuppliersSelected(): boolean {
    return (
      this.suppliers.length > 0 &&
      this.selectedSupplierCodes.length === this.suppliers.length
    );
  }

  toggleSelectAllSuppliers(event: any) {
    const isChecked = event.target.checked;
    if (isChecked) {
      this.selectedSupplierCodes = this.suppliers.map((s) => s.SupplierCode);
    } else {
      this.selectedSupplierCodes = [];
    }
    const val =
      this.selectedSupplierCodes.length > 0
        ? [this.selectedSupplierCodes.join(',')]
        : [];
    this.flightForm.get('FilterOptions.supplierCode')?.setValue(val);
  }

  selectPopular(item: any, type: 'from' | 'to') {
    if (type === 'to') {
      this.flightForm.patchValue({
        to: item.AirportCode,
        ui_To: item.AirportName + ' (' + item.AirportCode + ')',
      });
      this.showToPopular = false;
    } else {
      this.flightForm.patchValue({
        from: item.AirportCode,
        ui_From: item.AirportName + ' (' + item.AirportCode + ')',
      });
      this.showFromPopular = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: any) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.showFromPopular = false;
      this.showToPopular = false;
    }
  }

  onFlightTypeChange(type: 'oneway' | 'roundtrip' | 'multicity') {
    this.tripType = type;
    this.flightForm.get('tripType')?.setValue(type);
    const existing = this.originDestinations.at(0)?.value;
    while (this.originDestinations.length) this.originDestinations.removeAt(0);
    if (type === 'multicity') {
      this.MultiCityAdd(existing);
      this.MultiCityAdd();
    }
    this.setFlightTypeValidation(type);
  }

  setFlightTypeValidation(type: 'oneway' | 'roundtrip' | 'multicity') {
    if (type === 'oneway' || type === 'roundtrip') {
      // Single segment controls
      const fromControl = this.flightForm.get('from');
      const toControl = this.flightForm.get('to');

      fromControl?.clearValidators();
      toControl?.clearValidators();

      fromControl?.setValidators([Validators.required]);
      toControl?.setValidators([Validators.required]);

      fromControl?.updateValueAndValidity();
      toControl?.updateValueAndValidity();
    } else if (type === 'multicity') {
      // Multicity FormArray
      const fromControl = this.flightForm.get('from');
      const toControl = this.flightForm.get('to');

      fromControl?.clearValidators();
      toControl?.clearValidators();

      this.originDestinations.controls.forEach((od) => {
        const fromControl = od.get('from');
        const toControl = od.get('to');

        fromControl?.clearValidators();
        toControl?.clearValidators();

        fromControl?.setValidators([Validators.required]);
        toControl?.setValidators([Validators.required]);

        fromControl?.updateValueAndValidity();
        toControl?.updateValueAndValidity();
      });
    }
  }

  openFilter() {
    this.toggleFilter = !this.toggleFilter;
  }

  MultiCityAdd(existing?: any, isReturn: boolean = false) {
    const group = this.fb.group({
      from: [existing?.from || '', Validators.required],
      to: [existing?.to || '', Validators.required],
      departureDate: [
        isReturn ? null : existing?.departureDate || '',
        Validators.required,
      ],
      ui_From: [''],
      ui_To: [''],
    });
    this.originDestinations.push(group);
  }

  removeMultiCity(index: number) {
    if (this.originDestinations.length > 1)
      this.originDestinations.removeAt(index);
  }

  onAirportChange(event: any, type: 'from' | 'to') {
    if (type === 'from') {
      this.showFromPopular = false;
    } else {
      this.showToPopular = false;
    }
    if (this.isSwapping) return;
    const value = event.target.value.trim();
    if (value.length >= 3) this.searchAirport$.next(value);
    else {
      this.groupedAirports = [];
      this.autoTrigger?.closePanel();
    }
  }

  onAirlineChange(event: any) {
    const value = event.target.value.trim();
    if (value.length >= 2) {
      this.airlineList = this.airlines.filter(
        (airline: any) =>
          airline.iata &&
          airline.iata.trim() !== '' &&
          (airline.name?.toLowerCase().includes(value.toLowerCase()) ||
            airline.iata.toLowerCase().includes(value.toLowerCase())),
      );
    } else {
      this.airlineList = [];
    }
  }

  selectAirline(airline: any) {
    this.selectedAirline = airline;
    this.airlineList = [];
    this.flightForm
      .get('FilterOptions.PreferredAirlines')
      ?.setValue(airline.iata);
  }

  displayAirline(airline: any): string {
    if (!airline) return ''; // null/undefined case
    if (typeof airline === 'string') {
      // string case
      return airline;
    }
    return airline.name;
  }

  selectAirport(airport: Airport, type: 'from' | 'to', index: number = 0) {
    this.isOptionSelected = true;
    const group: any = this.originDestinations.at(index) as FormGroup;
    if (this.tripType != 'multicity') {
      if (type == 'from') {
        this.flightForm.get('from')?.setValue(airport.airportCode);
        this.flightForm
          .get('ui_From')
          ?.setValue(airport.airportName + '(' + airport.airportCode + ')');
      } else if (type == 'to') {
        this.flightForm.get('to')?.setValue(airport.airportCode);
        this.flightForm
          .get('ui_To')
          ?.setValue(airport.airportName + '(' + airport.airportCode + ')');
      }
      this.groupedAirports = [];
    } else {
      if (type == 'from') {
        group.get(type)?.setValue(airport.airportCode);
        group
          .get('ui_From')
          ?.setValue(airport.airportName + '(' + airport.airportCode + ')');
      } else if (type == 'to') {
        group.get(type)?.setValue(airport.airportCode);
        group
          .get('ui_To')
          ?.setValue(airport.airportName + '(' + airport.airportCode + ')');
      }
      this.groupedAirports = [];
    }
    setTimeout(() => {
      this.isOptionSelected = false;
    }, 500);
  }

  displayFn(airport: any): string {
    if (!airport) return ''; // null/undefined case
    if (typeof airport === 'string') {
      // string case
      return airport.trim() === '' ? '' : airport;
    }
    if (airport.airportName && airport.airportCode) {
      return `${airport.airportName} (${airport.airportCode})`;
    }
    return ''; // last fallback
  }

  setFormValue(control: any, value: any) {
    this.flightForm.controls[control].setValue(value);
  }

  updatePassenger(
    type: 'adult' | 'child' | 'infant',
    action: 'add' | 'remove',
  ) {
    const control = this.flightForm.get(['passengers', type]);
    if (!control) return;

    let value = control.value;
    if (action === 'add') {
      if (
        (type === 'adult' && value < 9) ||
        (type === 'child' && value < 8) ||
        (type === 'infant' && value < 4)
      )
        control.setValue(value + 1);
    } else {
      if ((type === 'adult' && value > 1) || value > 0)
        control.setValue(value - 1);
    }
    this.updatePassengerLabel();
  }

  private updatePassengerLabel() {
    const passengers = this.flightForm.get('passengers')?.value;
    this.totalPassengers =
      passengers.adult + passengers.child + passengers.infant;
  }

  async onDateChange(
    event: MatDatepickerInputEvent<any>,
    index: number = 0,
    type: 'departureDate' | 'returnDate' = 'departureDate',
  ) {
    const formattedDate = await this.datePipe.transform(event.value);

    if (this.tripType === 'multicity') {
      const control = this.originDestinations.at(index)?.get('departureDate');
      this.journeyDateMulti[index] = event.value;
      if (control) control.setValue(formattedDate);
    } else {
      if (type === 'departureDate') {
        this.setFormValue('departureDate', formattedDate);
        this.setFormValue('ui_DepartureDate', formattedDate);
        this.journeyDate = formattedDate;
        this.journeyDayName = this.getDayName(event.value);
      } else if (type === 'returnDate' && this.tripType === 'roundtrip') {
        this.setFormValue('returnDate', formattedDate);
        this.setFormValue('ui_ReturnDate', formattedDate);
        this.returnDate = event.value;
        this.returnDayName = this.getDayName(event.value);
      }
    }
  }

  private getDayName(date: Date | null): string {
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  onResultTypeChange(event: any) {
    const isChecked = event.target.checked;
    const resultTypeControl = this.flightForm
      .get('FilterOptions')
      ?.get('ResultType');
    if (isChecked) {
      resultTypeControl?.setValue('Separate');
    } else {
      resultTypeControl?.setValue('Combined');
    }
  }

  onDirectFlightOnlyChange(event: any) {
    const isChecked = event.target.checked;
    const directFlightOnlyControl = this.flightForm
      .get('FilterOptions')
      ?.get('DirectFlightOnly');
    if (isChecked) {
      directFlightOnlyControl?.setValue(true);
    } else {
      directFlightOnlyControl?.setValue(false);
    }
  }

  async onSubmit() {
    if (!this.flightForm.valid) {
      this.notification.error('Please fill all required fields correctly.');
      return;
    }

    this.loading = true;

    try {
      const currData = await this.dbService.getData('userCurr');

      const generalInfo = this.flightForm.get('generalInfo') as FormGroup;

      generalInfo
        .get('grossCurrency')
        ?.setValue(currData?.TargetCurrency || 'AED');
      generalInfo
        .get('agencyCurrency')
        ?.setValue(currData?.TargetCurrency || 'AED');
      generalInfo
        .get('channel')
        ?.setValue(AppConstants_settings.CHANNEL || 'B2B');

      const tokenRaw = await this.dbService.getData('tokenData');

      if (tokenRaw) {
        const userData = tokenRaw;
        generalInfo
          .get('grossCurrency')
          ?.setValue(userData?.AgencyCurrency || 'AED');
        generalInfo
          .get('agencyCurrency')
          ?.setValue(userData?.AgencyCurrency || 'AED');
        generalInfo.get('agentId')?.setValue(userData?.AgentId || 0);
        generalInfo
          .get('companyBranchId')
          ?.setValue(userData?.CompanyBranchId || 0);
        generalInfo.get('agencyUserId')?.setValue(userData?.AgencyUserId || 0);
        generalInfo
          .get('agencyBranchId')
          ?.setValue(userData?.AgencyBranchId || 0);
      }

      const payload = { ...this.flightForm.value };
      if (payload.FilterOptions) {
        // Deep copy FilterOptions so we do not mutate the form's underlying reference
        payload.FilterOptions = { ...payload.FilterOptions };

        if (this.showSupplierToAgency) {
          let codes: string[] = [];
          const rawVal = payload.FilterOptions.supplierCode || payload.FilterOptions.SupplierCode;
          if (Array.isArray(rawVal) && rawVal.length > 0) {
            if (rawVal.length === 1 && typeof rawVal[0] === 'string' && rawVal[0].includes(',')) {
              codes = rawVal[0].split(',').map((s: string) => s.trim()).filter((s: string) => s !== '');
            } else {
              codes = rawVal.map((s: any) => String(s).trim());
            }
          }
          if (codes.length === 0 && this.selectedSupplierCodes && this.selectedSupplierCodes.length > 0) {
            codes = this.selectedSupplierCodes;
          }
          payload.FilterOptions.SupplierCode = codes;
        } else {
          payload.FilterOptions.SupplierCode = [];
        }
        delete payload.FilterOptions.supplierCode;
      }

      this.apiService.flightRequest(payload).subscribe({
        next: (res: any) => {
          this.loading = false;

          if (res?.statuscode == 1) {
            this.dbService.setData('request-data', payload);

            const resultId = JSON.parse(res.jsondata).resultId;

            this.router.navigate(['/flight-response'], {
              queryParams: { resId: resultId },
            });
          }
        },
        error: (err) => {
          this.loading = false;
          const msg =
            err?.error?.message ||
            'Something went wrong while searching flight';
          this.notification.error(msg);
        },
      });
    } catch (err) {
      this.loading = false;
      console.error(err);
      this.notification.error('Data fetch failed');
    }
  }
}
